import { useCallback, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FaExpand } from 'react-icons/fa';

import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import 'yet-another-react-lightbox/plugins/counter.css';

import { img } from '../lib/img';
import { useTheme } from '../lib/theme';

// Kafelek ze „szkieletem" — miejsce jest zarezerwowane, zdjęcie pojawia się płynnie.
const Tile = ({ src, alt, index, onOpen, layout, thumbWidth, isImperial }) => {
  const [loaded, setLoaded] = useState(false);
  const square = layout === 'grid';

  return (
    <motion.button
      type="button"
      onClick={() => onOpen(index)}
      aria-label={alt}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.4), ease: 'easeOut' }}
      className={`group relative block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 focus-visible:ring-offset-transparent
        ${square ? 'aspect-square' : 'mb-4 break-inside-avoid'}
        ${isImperial ? 'rounded-none' : 'rounded-lg'}`}
    >
      {!loaded && <span className="ph-shimmer absolute inset-0 block" aria-hidden />}

      <img
        src={img(src, { w: thumbWidth })}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`block w-full transition-all duration-700 ease-out group-hover:scale-[1.06]
          ${square ? 'h-full object-cover' : 'h-auto'}
          ${loaded ? 'opacity-100 blur-0' : 'opacity-0 blur-sm'}`}
      />

      {/* nakładka na hover */}
      <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent opacity-0 transition-opacity duration-400 group-hover:opacity-100" aria-hidden />
      <span
        className={`pointer-events-none absolute inset-0 transition-colors duration-500 ${
          isImperial ? 'ring-1 ring-inset ring-brand-accent/0 group-hover:ring-brand-accent/60' : 'ring-0'
        }`}
        aria-hidden
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-400 group-hover:opacity-100" aria-hidden>
        <span className="flex h-11 w-11 translate-y-2 items-center justify-center rounded-full border border-brand-accent/70 bg-black/40 text-brand-accent backdrop-blur-sm transition-transform duration-400 group-hover:translate-y-0">
          <FaExpand className="h-3.5 w-3.5" />
        </span>
      </span>
    </motion.button>
  );
};

/**
 * Wspólna galeria: siatka/mozaika + nowoczesny lightbox
 * (zoom, miniatury, licznik, pełny ekran, pokaz slajdów, gesty, klawiatura).
 */
const PhotoGallery = ({
  images = [],
  alt = 'Zdjęcie',
  layout = 'masonry',       // 'masonry' | 'grid'
  thumbWidth = 500,
  emptyText = null,
}) => {
  const { isImperial } = useTheme();
  const [index, setIndex] = useState(-1);

  const items = useMemo(
    () => images.filter(Boolean).map((it) => (typeof it === 'string' ? { url: it } : it)),
    [images]
  );

  const slides = useMemo(
    () =>
      items.map((it) => ({
        src: img(it.url, { w: 1920 }),
        alt: it.caption || alt,
        // warianty dla zoomu / ekranów HiDPI
        srcSet: [640, 1024, 1600, 2048].map((w) => ({ src: img(it.url, { w }), width: w })),
      })),
    [items, alt]
  );

  const open = useCallback((i) => setIndex(i), []);

  if (!items.length) {
    return emptyText ? <p className="col-span-full text-center text-brand-text-light/70">{emptyText}</p> : null;
  }

  const containerClass =
    layout === 'grid'
      ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4'
      : 'columns-2 gap-3 sm:columns-2 sm:gap-4 md:columns-3 lg:columns-4';

  return (
    <>
      <div className={containerClass}>
        {items.map((it, i) => (
          <Tile
            key={`${it.url}-${i}`}
            src={it.url}
            alt={it.caption || `${alt} ${i + 1}`}
            index={i}
            onOpen={open}
            layout={layout}
            thumbWidth={thumbWidth}
            isImperial={isImperial}
          />
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={slides}
        plugins={[Zoom, Thumbnails, Counter, Fullscreen, Slideshow]}
        carousel={{ finite: false, padding: 0 }}
        controller={{ closeOnBackdropClick: true }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        thumbnails={{ width: 96, height: 64, border: 0, gap: 8, padding: 0 }}
        slideshow={{ delay: 4000 }}
        animation={{ fade: 300, swipe: 400 }}
        styles={{
          container: { backgroundColor: 'rgba(8, 8, 10, 0.96)' },
          thumbnailsContainer: { backgroundColor: 'rgba(8, 8, 10, 0.96)' },
        }}
      />
    </>
  );
};

export default PhotoGallery;
